//EXTERNAL
import { useMemo } from "react";
import { Link, useHistory } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
//COMPONENTS
import * as ComponentLibrary from "@picnicmedia/masanatemples123";
import { useCreativeActions } from "./creativeActions";
//CONTEXT
import { useUserState } from "../../../../contexts/user.context";
import { useCreativeDispatch } from "../../../../contexts/creative.context";
// LIBS
import { Google_Analytics_Event } from "../../../../libs/google-analytics";
import { analytics_record } from "../../../../libs/amplify";
//HELPERS
import { create_new_time_isostring, format_time } from "../../../../helpers/format_time";
import { setCreativeTTB } from "../../../../helpers/time_to_build";
//CONFIGS
import config from "../../../../configs/config";
import studio_config from "../../../../configs/studio.json";
//TYPES
import { ColumnsDataProps } from "../types";
import { PreviewButton } from "../../preview";

export interface StudioConfig {
  [key: string]: any;
}

// Controls what information Table component renders
export function useColumnsData({ getPreviews, handleSpecificAdvertiserLink, deleteCreativeModal, setDeleteCreativeModal, advertiserId, requestTableData, queryVariables, handleOpenReportingModal, sort, refreshCreatives }: ColumnsDataProps) {
  const user = useUserState();
  const creativeDispatch = useCreativeDispatch();
  const studioConfig: StudioConfig = studio_config;
  const history = useHistory();
  const { handleDuplicateCreative, openDeleteCreativeModal } = useCreativeActions({ getPreviews, deleteCreativeModal, setDeleteCreativeModal, advertiserId, queryVariables, refreshCreatives });
  /**
   * JA (04-09-23)
   * sortOption: Finds the first sorting preference ('asc' or 'desc') that is set to true in the sortOrder object.
   */
  const sortOption = Object.keys(sort).find((key) => sort[key as keyof typeof sort]);

  /**
   * JH (29-07-26)
   * Bring-your-own package creatives (metadata.kind === "package") have no studio.json format entry —
   * their format is "custom" — so the frame-ratio lookups every template row relies on would throw on
   * them. These helpers keep package rows off that path: they aren't gated by supported ratios, they
   * can't be opened in the template editor, and they preview straight from their unpacked S3 index.html.
   */
  // Treat a row as a package if it says so, OR if its format isn't a known studio format — the latter
  // keeps the table from crashing on a package row even if the server hasn't redeployed the schema that
  // exposes metadata.kind yet (a "custom"-format package would otherwise hit studioConfig.formats["custom"]).
  const isPackage = (metadata: any) => metadata?.kind === "package" || !studioConfig.formats[metadata?.format];
  const cellDisabled = (metadata: any) => {
    if (isPackage(metadata)) return false;
    const supportedRatios = studioConfig.formats[metadata.format].media.frameRatios.filter((frameRatio: FrameRatio) => frameRatio.active);
    return !supportedRatios.some((frameRatio: FrameRatio) => frameRatio.name === metadata.aspectRatio) || !studioConfig.activeFormats.includes(metadata.format);
  };
  const packagePreviewUrl = (creativeId: string) => `https://${config.s3.BUCKET}.s3.${config.s3.REGION}.amazonaws.com/public/${user.teamId}/${creativeId}/index.html`;

  const adsColumns = useMemo(
    () => [
      {
        id: "preview-thumbnail",
        Header: "Preview",
        accessor: "previewThumbnail",
        Cell: (data: any) => {
          const row = data.row.original;
          const pkg = isPackage(row.metadata);
          const cellIsDisabled = cellDisabled(row.metadata);
          const clickable = pkg || !cellIsDisabled;
          return (
            <div
              style={{ cursor: clickable ? "pointer" : "not-allowed" }}
              onClick={() => {
                if (pkg) return window.open(packagePreviewUrl(row.id));
                if (cellIsDisabled) return null;
                history.push(`/creative/${row.id}`);
              }}>
              {row.previewThumbnail}
            </div>
          );
        },
      },
      {
        id: "ad-name",
        Header: "Ad name",
        width: 400,
        Cell: (data: any) => {
          const row = data.row.original;
          const advertiserName = row.advertiserName;
          const adName = row.adName;
          const pkg = isPackage(row.metadata);
          const cellIsDisabled = cellDisabled(row.metadata);
          return (
            <>
              <div className={"text-size-2 mid-gray fw5"}>{advertiserName}</div>
              <div className={"text-size-3 fw5 flex flex-row items-center"}>
                {adName}
                {!pkg && cellIsDisabled && <ComponentLibrary.PCTooltip message={"Creative is available for preview only"} color={"color-neutral-3"} type={"dark"} icon={"info-circle"} id={"icons-tooltip"} />}
              </div>
            </>
          );
        },
      },
      {
        id: "creative-type",
        Header: "Creative Type",
        accessor: "metadata.format",
        width: 200,
        Cell: (data: any) => {
          const metadata = data.row.original.metadata;
          if (isPackage(metadata)) {
            const dims = metadata.dimensions;
            return dims?.width && dims?.height ? `Custom (${dims.width}×${dims.height})` : "Custom";
          }
          let value = data.cell.value;
          if (value === "swipe") value = "swipe Right";
          return value[0].toUpperCase() + value.substring(1);
        },
      },
      {
        id: "last-updated",
        Header: "Last Updated",
        accessor: "updatedAt",
        width: 200,
        Cell: (data: any) => format_time(data.cell.value),
      },
      {
        id: "actions",
        Header: "Actions",
        accessor: "id",
        width: 52,
        Cell: (data: any) => <PreviewButton isTruncated={true} data={data.row.original} />,
      },
      {
        id: "pencil",
        Header: " ",
        width: 52,
        Cell: (data: any) => {
          const row = data.row.original;
          // Packages aren't editable in the template editor — preview/delete only.
          const cellIsDisabled = isPackage(row.metadata) || cellDisabled(row.metadata);
          return <ComponentLibrary.PCIconButton icon={"pencil"} title={"Edit"} clickHandler={() => (cellIsDisabled ? null : history.push(`/creative/${row.id}`))} utilityClasses={cellIsDisabled ? "bg-light-gray" : "bg-pink white b--pink"} inlineStyles={{ height: "40px", width: "40px" }} isDisabled={cellIsDisabled} />;
        },
      },
      {
        id: "duplicate",
        Header: "",
        width: 52,
        accessor: "duplicate",
        Cell: (data: any) => {
          const row = data.row.original;
          // Duplicate goes through the template-creative factory, which packages don't fit — disable for now.
          const cellIsDisabled = isPackage(row.metadata) || cellDisabled(row.metadata);
          return (
            <ComponentLibrary.PCIconButton
              isDisabled={cellIsDisabled}
              icon={"clone"}
              title={"Duplicate"}
              clickHandler={(e: any) => {
                if (cellIsDisabled) return;
                handleDuplicateCreative(e, row.id);
              }}
              utilityClasses={"b--black-20"}
              inlineStyles={{ height: "40px", width: "40px" }}
            />
          );
        },
      },
      ...(!config.managedServiceTeamIds.includes(user.teamId)
        ? [
            {
              id: "reporting",
              Header: "",
              accessor: "reporting",
              width: 52,
              Cell: (data: any) => {
                const row = data.row.original;
                // Packages have no Adform tags yet (v1), so per-banner reporting isn't available.
                const cellIsDisabled = isPackage(row.metadata) || cellDisabled(row.metadata);

                return (
                  <ComponentLibrary.PCIconButton
                    clickHandler={async (e: any) => {
                      e.preventDefault();
                      if (cellIsDisabled) return;
                      await handleOpenReportingModal?.("banner", row.id);
                    }}
                    icon={"chart-column"}
                    title={"Generate Report"}
                    utilityClasses={"b--black-20"}
                    inlineStyles={{ height: "40px", width: "40px" }}
                    isDisabled={cellIsDisabled}
                  />
                );
              },
            },
          ]
        : []),
      {
        id: "delete",
        Header: "",
        accessor: "delete",
        width: 52,
        Cell: (row: any) => <ComponentLibrary.PCIconButton title={"Delete"} clickHandler={(e: any) => openDeleteCreativeModal(e, row.cell.row.original.id)} icon={"trash"} utilityClasses={"b--black-20"} inlineStyles={{ height: "40px", width: "40px" }} />,
      },
    ],
    [requestTableData],
  );

  const brandsColumns = useMemo(
    () => [
      {
        id: "advertiser",
        Header: "Advertiser",
        width: 550,
        Cell: (data: any) => {
          const advertiserName = data.row.original.advertiserName;
          const advertiserId = data.row.original.advertiserId;
          return (
            <>
              <Link
                className="no-underline black"
                onClick={() => handleSpecificAdvertiserLink(advertiserId, advertiserName)}
                to={{
                  pathname: `${advertiserId}/${advertiserName}`,
                  search: `?adName=&page=1&sort=${sortOption}`,
                }}>
                <div className={"text-size-3 fw5"}>{advertiserName}</div>
              </Link>
            </>
          );
        },
      },
      {
        id: "ads",
        Header: "Ads",
        accessor: "adsCount",
        width: 150,
        Cell: (data: any) => {
          return <div className={"text-size-2 mid-gray fw5"}>{data.cell.value}</div>;
        },
      },
      {
        id: "last-updated",
        Header: "Last Updated",
        accessor: "updatedAt",
        width: 300,
        Cell: (data: any) => {
          return format_time(data.cell.value);
        },
      },
      {
        id: "add-creative",
        Header: "Actions",
        width: 100,
        Cell: (data) => {
          const NEWCREATIVEID = uuidv4();
          return (
            <div className="relative">
              <Link
                className="no-underline white"
                to={{
                  pathname: `/creative/${NEWCREATIVEID}`,
                  search: "?new",
                }}>
                <ComponentLibrary.PCIconButton
                  utilityClasses={"background-color-primary-3 white b--pink"}
                  clickHandler={() => {
                    Google_Analytics_Event({ name: "create_new_ad", category: "ad_creative", action: "start", label: NEWCREATIVEID });
                    setCreativeTTB("id", NEWCREATIVEID);
                    setCreativeTTB("startTime", create_new_time_isostring());
                    if (config.analytics.DISABLED === false && user.teamId !== "pi6j") {
                      analytics_record({
                        name: `createad_start`,
                        attributes: {
                          eventType: "click",
                          userId: `${user.id}`,
                          userEmail: `${user.email}`,
                          creativeId: NEWCREATIVEID,
                          teamId: `${user.teamId}`,
                          teamName: `${user.teamName}`,
                          advertiserId: `${data.row.original.advertiserId}`,
                          advertiserName: `${data.row.original.advertiserName}`,
                        },
                      });
                    }
                    creativeDispatch({
                      type: "RESET_CREATIVE_DATA",
                      payload: undefined,
                    });
                    // history.push(`/creative/${NEWCREATIVEID}?new`);
                  }}
                  icon={"plus"}
                  inlineStyles={{ height: "40px", width: "40px" }}
                />
              </Link>
            </div>
          );
        },
      },
    ],
    [requestTableData],
  );
  return { adsColumns, brandsColumns };
}
